/**
 * Machine page › Parts & stock.
 *
 * The demo's `machineTab('parts')` (line 2228): every catalogue part that fits
 * this machine's class, with criticality, list price, stock at the site store
 * and at the serving distribution centre, and lead time. The site figure is red
 * when it is below the minimum holding.
 *
 * Domain: `parts` (`parts` + `inventory`).
 *
 * UI layer.
 *
 * @see src/versions/v1/modules/fleet/MachineScene.transform.ts
 * @see docs/logic/fleet/machine-page.md
 */

import { useMemo } from 'react';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { groupNum } from '../../shared/oem-engine';
import { useOemData } from '../../shared/useOemData';
import { TabLoader } from './TabLoader';
import { machineParts } from './MachineScene.transform';

export interface MachinePartsTabProps {
  machineId: string;
}

export function MachinePartsTab({ machineId }: MachinePartsTabProps) {
  const { data, loading, error } = useOemData('parts');

  const view = useMemo(
    () => (data ? machineParts(machineId, data.parts, data.inventory) : null),
    [data, machineId],
  );

  return (
    <TabLoader loading={loading || !view} error={error} what="Parts and stock" rows={10}>
      {view ? (
        <div className="space-y-2">
          <p className="text-sm text-muted">
            <b className="text-zinc-900 dark:text-zinc-100">{view.rows.length}</b> parts fit{' '}
            {machineId} ·{' '}
            <b className={view.belowMin ? 'text-[var(--crit)]' : undefined}>{view.belowMin}</b>{' '}
            below minimum at the {view.store}.
          </p>
          <Table>
            <THead>
              <TR>
                <TH>Part</TH>
                <TH>Description</TH>
                <TH>Criticality</TH>
                <TH className="text-right">List A$</TH>
                <TH className="text-right">{view.store}</TH>
                <TH className="text-right">{view.dc}</TH>
                <TH className="text-right">Lead time (days)</TH>
              </TR>
            </THead>
            <TBody>
              {view.rows.map((row) => (
                <TR key={row.part.part_no}>
                  <TD className="mono whitespace-nowrap">{row.part.part_no}</TD>
                  <TD>{row.part.description}</TD>
                  <TD>
                    <span
                      className={`band-pill ${row.part.criticality === 'Critical' ? 'band-vpoor' : 'band-fair'}`}
                    >
                      {row.part.criticality}
                    </span>
                  </TD>
                  <TD className="mono text-right">{groupNum(row.part.list_price_aud)}</TD>
                  <TD
                    className={`mono text-right ${row.belowMin ? 'font-semibold text-[var(--crit)]' : ''}`}
                  >
                    {row.site ? `${row.site.qty_on_hand} (min ${row.site.min_level})` : '—'}
                  </TD>
                  <TD className="mono text-right">{row.dc ? row.dc.qty_on_hand : '—'}</TD>
                  <TD className="mono text-right">{row.part.lead_time_days}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <p className="text-[11px] text-muted">
            Salesforce parts-portal snapshot 09 Sep 06:00. Red = below minimum at the site store.
          </p>
        </div>
      ) : null}
    </TabLoader>
  );
}
